/**
 * reservationsV2Demo — capa de reserva para la vista previa "Reservas V2".
 *
 * Qué añade y qué NO:
 *   - Las reservas, las unidades, los bloqueos y las incidencias siguen
 *     viniendo de `pmsDemoData` / `pmsRuntimeStore`. Aquí NO hay un segundo
 *     conjunto de reservas: solo lo que el modelo V1 todavía no expresa.
 *   - Las peticiones y los pedidos de servicio tampoco se declaran aquí: ya
 *     viven en `todayV2Demo` / `todayV2Store`, que es el sitio del que leen
 *     tanto Hoy como Reservas. Una sola verdad.
 *   - Las personas tampoco: el huésped y sus acompañantes viven en
 *     `guestsV2Demo`, con su propio `guest_id`. Aquí no hay una segunda copia
 *     de ningún email ni de ningún nombre.
 *   - Lo propio de esta capa: referencia externa del canal, eventos de pago,
 *     mensajes y notas. Lo del contrato, no lo de la gente.
 *
 * Riqueza declarada solo para unas pocas reservas. El resto se deriva con
 * reglas documentadas (`reservationsV2Selectors`) en vez de inventar un dato
 * distinto por reserva: un marcador de posición honesto vale más que 31 fichas
 * falsamente detalladas.
 *
 * Nada de este módulo llama a ningún endpoint.
 */

// ---------------------------------------------------------------------------
// Filtros
// ---------------------------------------------------------------------------

/**
 * Filtros rápidos en el idioma del operador. No exponen nombres técnicos de
 * estado: "Pendiente de cobro" es más útil que `payment_status=unpaid`.
 */
export const QUICK_FILTERS = [
  { key: "today", label: "Hoy" },
  { key: "arrivals", label: "Llegadas" },
  { key: "departures", label: "Salidas" },
  { key: "in_house", label: "Alojados" },
  { key: "upcoming", label: "Próximas" },
  { key: "unassigned", label: "Sin asignar" },
  { key: "unpaid", label: "Pendiente de cobro" },
  { key: "attention", label: "Necesita atención" },
  { key: "cancelled", label: "Canceladas" },
];

/** Resumen de cabecera: contexto, no cuadro de mando. */
export const SUMMARY_KEYS = ["today", "upcoming", "in_house", "departures", "attention"];

export const SUMMARY_LABEL = {
  today: "Hoy",
  upcoming: "Próximas",
  in_house: "Alojados",
  departures: "Salidas",
  attention: "Necesita atención",
};

// ---------------------------------------------------------------------------
// Pagos (resumen de reserva — esto NO es contabilidad)
// ---------------------------------------------------------------------------

export const PAYMENT_EVENT_KINDS = {
  deposit: { label: "Depósito recibido", sign: 1 },
  final: { label: "Pago final", sign: 1 },
  adjustment: { label: "Ajuste", sign: 1 },
  refund: { label: "Reembolso", sign: -1 },
};

/**
 * Límite económico de una reserva.
 *
 * Lo que SÍ pertenece al nivel de reserva (y es lo único que se calcula aquí):
 *   ingreso bruto de alojamiento
 *   + servicios y extras de la reserva
 *   − PBJT / impuesto de alojamiento
 *   − comisión de OTA cuando el canal es una OTA
 *   − coste de procesamiento de pago cuando se cobra por nuestra pasarela
 *   = ingreso neto de la reserva
 *
 * Lo que NO se calcula nunca en Reservas: está en `ECONOMICS_OUT_OF_SCOPE`.
 * Son costes del periodo, no de una estancia, y reparten con otras reglas.
 */
export const ECONOMICS_OUT_OF_SCOPE = [
  "Sueldos del equipo",
  "Electricidad y suministros",
  "Mantenimiento general",
  "Seguros",
  "Otros gastos operativos",
  "Comisión de gestión",
  "Beneficio distribuible al propietario",
];

/** Dónde vive de verdad ese cálculo. La comisión de gestión sigue sin fijarse. */
export const ECONOMICS_OUT_OF_SCOPE_NOTE =
  "Lo de arriba es el resultado de UNA reserva. Los gastos del periodo y el "
  + "reparto con el propietario se calculan en Finanzas \u00b7 Cierre mensual, no aqu\u00ed. "
  + "La comisi\u00f3n de gesti\u00f3n no se fija en esta vista previa.";

export const PAYMENT_METHOD_LABEL = {
  card: "Tarjeta",
  bank_transfer: "Transferencia",
  cash: "Efectivo",
  channel: "Cobrado por el canal",
  other: "Otro",
};

/**
 * Texto para un cobro parcial del que NO se conoce el importe.
 *
 * Aquí no se reparte un cobro parcial con un porcentaje de depósito supuesto:
 * eso fabricaría dinero que nadie ha cobrado. Si una reserva tiene que enseñar
 * un pago parcial, se declara su movimiento en `RESERVATION_EXTRAS`; si no hay
 * movimiento, el importe sencillamente no se sabe y se dice.
 */
export const PARTIAL_AMOUNT_UNKNOWN_LABEL = "Cobro parcial \u00b7 importe no disponible";

// ---------------------------------------------------------------------------
// Mensajes y actividad
// ---------------------------------------------------------------------------

/**
 * La conversación con el huésped NO existe todavía: PRODUCT_V2_SPEC deja
 * pendiente el modelo de contacto y conversación, y el WhatsApp que hay en el
 * sistema es el de los leads de venta, con otras reglas. Esta línea de tiempo
 * es una maqueta y no envía nada.
 */
export const MESSAGE_KINDS = {
  automated: { label: "Automático", tone: "stone" },
  staff: { label: "Equipo", tone: "blue" },
  guest: { label: "Huésped", tone: "emerald" },
};

export const ACTIVITY_LABEL = {
  created: "Reserva creada",
  assigned: "Unidad asignada",
  unassigned: "Unidad liberada",
  payment: "Pago registrado",
  pre_checkin: "Check-in online recibido",
  service: "Servicio añadido",
  service_removed: "Servicio retirado",
  checked_in: "Entrada registrada",
  checked_out: "Salida registrada",
  status: "Estado actualizado",
  cancelled: "Reserva cancelada",
  override: "Asignación manual",
};

// ---------------------------------------------------------------------------
// Cancelaciones
// ---------------------------------------------------------------------------

export const CANCELLED_BY_LABEL = {
  guest: "El huésped",
  staff: "El equipo",
  channel: "El canal",
  no_show: "No-show",
};

export const CANCELLATION_REASONS = [
  "Cambio de planes del huésped",
  "Problema de vuelo",
  "Duplicada",
  "Impago",
  "Incidencia en la unidad",
  "Otro",
];

// ---------------------------------------------------------------------------
// Referencia externa del canal
// ---------------------------------------------------------------------------

/**
 * Prefijo de la referencia que da cada canal. Cuando exista channel manager,
 * la referencia llegará del proveedor; hoy se compone para poder enseñar el
 * campo. Ningún proveedor está conectado.
 */
export const EXTERNAL_REF_PREFIX = {
  booking: "BDC",
  airbnb: "ABNB",
  agoda: "AGD",
  direct_web: "WEB",
  whatsapp: "WA",
  manual: "MAN",
  walk_in: "WLK",
};

/** Canales que, en el futuro, entrarán por un channel manager y no a mano. */
export const CHANNEL_MANAGER_CHANNELS = ["booking", "airbnb", "agoda"];


// ---------------------------------------------------------------------------
// Riqueza declarada por reserva
// ---------------------------------------------------------------------------

/**
 * Solo para las reservas que cuentan una historia concreta: referencia del
 * canal, notas, mensajes y movimientos de cobro.
 *
 * AQUÍ NO HAY PERSONAS. El contacto del huésped y los acompañantes vivían
 * también en este objeto (`guest`, `companions`) al mismo tiempo que en
 * `guestsV2Demo.GUESTS`, y dos copias del mismo email son dos emails que
 * acaban siendo distintos. Las personas son del modelo de huésped, que es
 * donde tienen `guest_id`; `guestContactFor()` deriva de allí lo que la
 * búsqueda de reservas todavía necesita.
 *
 * Para las reservas que no declaran nada no se compone ningún contacto: si el
 * dato no está, la ficha dice que no está. Un email inventado se copia, se
 * pega y acaba en un correo de verdad, así que lo que falta se queda faltando.
 */
export const RESERVATION_EXTRAS = {
  // Llega hoy, con el check-in online a medias y el cobro sin capturar.
  R2028006: {
    external_ref: "BDC-4471902",
    notes: "Pidió planta baja si es posible. Sin confirmar.",
    messages: [
      { at: "2028-08-01T14:35:00Z", kind: "automated", channel: "email", title: "Confirmación de reserva", preview: "Tu reserva en Dolce Villas Lombok está confirmada." },
      { at: "2028-08-18T09:00:00Z", kind: "automated", channel: "email", title: "Recordatorio de check-in online", preview: "Faltan datos para preparar tu llegada." },
      { at: "2028-08-20T17:12:00Z", kind: "guest", channel: "portal", title: "Mensaje del huésped", preview: "Llegaremos sobre las 16:30, venimos desde Bali." },
    ],
    payment_events: [],
  },
  // Pagada, con asignación forzada a mano y reporte de inmigración pendiente.
  R2028009: {
    external_ref: "BDC-4488130",
    notes: "Huéspedes recurrentes. Pidieron V03 expresamente.",
    messages: [
      { at: "2028-07-30T09:05:00Z", kind: "automated", channel: "email", title: "Confirmación de reserva", preview: "Reserva confirmada para el 21 de agosto." },
      { at: "2028-08-19T10:20:00Z", kind: "staff", channel: "email", title: "Instrucciones de llegada", preview: "Os esperamos a las 14:00; el acceso es por la puerta lateral." },
    ],
    payment_events: [
      { at: "2028-07-30T09:10:00Z", kind: "final", amount_eur: 1470, method: "channel", note: "Cobrado por el canal" },
    ],
  },
  // Llegada nocturna con traslado sin confirmar.
  R2028024: {
    external_ref: "BDC-4501774",
    notes: "Vuelo GA-256, llegada prevista 21:40.",
    messages: [
      { at: "2028-07-14T11:05:00Z", kind: "automated", channel: "email", title: "Confirmación de reserva", preview: "Reserva confirmada del 21 al 30 de agosto." },
      { at: "2028-08-20T18:25:00Z", kind: "guest", channel: "portal", title: "Petición del huésped", preview: "¿Podéis recogernos en el aeropuerto?" },
    ],
    payment_events: [
      { at: "2028-07-14T11:10:00Z", kind: "final", amount_eur: 2340, method: "channel", note: "Cobrado por el canal" },
    ],
  },
  // Solicitud por WhatsApp sin confirmar ni cobrar.
  R2028022: {
    external_ref: null,
    notes: "Pidió precio por WhatsApp. Pendiente de confirmar y de cobrar.",
    messages: [
      { at: "2028-08-15T08:12:00Z", kind: "guest", channel: "whatsapp", title: "Consulta del huésped", preview: "¿Tenéis disponible del 28 al 31?" },
      { at: "2028-08-15T08:40:00Z", kind: "staff", channel: "whatsapp", title: "Respuesta del equipo", preview: "Sí, os reservo provisionalmente hasta mañana." },
    ],
    payment_events: [],
  },
  // Estancia en curso con peticiones abiertas.
  R2028003: {
    external_ref: "WEB-2028-0731",
    notes: null,
    messages: [
      { at: "2028-07-24T11:05:00Z", kind: "automated", channel: "email", title: "Confirmación de reserva", preview: "Reserva confirmada del 19 al 26 de agosto." },
      { at: "2028-08-19T13:40:00Z", kind: "automated", channel: "whatsapp", title: "Instrucciones de llegada", preview: "Tu villa está lista. Código de acceso en el portal." },
    ],
    payment_events: [
      { at: "2028-07-24T11:10:00Z", kind: "deposit", amount_eur: 441, method: "card", note: "Depósito al reservar" },
      { at: "2028-08-19T14:05:00Z", kind: "final", amount_eur: 1029, method: "card", note: "Resto a la llegada" },
    ],
  },
  // Cancelada: se conserva entera, con motivo y reembolso.
  R2028029c: {
    external_ref: "ABNB-HMXK2028",
    notes: null,
    messages: [
      { at: "2028-06-15T09:05:00Z", kind: "automated", channel: "email", title: "Confirmación de reserva", preview: "Reserva confirmada del 10 al 14 de agosto." },
      { at: "2028-08-01T10:05:00Z", kind: "automated", channel: "email", title: "Cancelación confirmada", preview: "Tu reserva ha sido cancelada." },
    ],
    payment_events: [
      { at: "2028-06-15T09:10:00Z", kind: "final", amount_eur: 1040, method: "channel", note: "Cobrado por el canal" },
      { at: "2028-08-02T11:00:00Z", kind: "refund", amount_eur: 520, method: "channel", note: "Reembolso según política moderada" },
    ],
  },
  // Cobro parcial pendiente de completar.
  R2028031: {
    external_ref: "MAN-2028-0810",
    notes: "Transferencia parcial recibida. Resto a la llegada.",
    messages: [
      { at: "2028-08-10T12:05:00Z", kind: "staff", channel: "email", title: "Datos para la transferencia", preview: "Te enviamos los datos bancarios para el depósito." },
    ],
    payment_events: [
      { at: "2028-08-12T09:30:00Z", kind: "deposit", amount_eur: 312, method: "bank_transfer", note: "Depósito por transferencia" },
    ],
  },
};

// ---------------------------------------------------------------------------
// Nueva reserva (asistente de demostración)
// ---------------------------------------------------------------------------

export const WIZARD_STEPS = [
  { key: "stay", label: "Estancia" },
  { key: "guest", label: "Huésped" },
  { key: "pricing", label: "Precio" },
  { key: "assignment", label: "Asignación" },
  { key: "confirm", label: "Confirmar" },
];

/**
 * Origen de un alta manual. Las OTA no se teclean: entrarán por el channel
 * manager cuando exista, y ninguno está contratado todavía.
 */
export const MANUAL_ORIGINS = [
  { value: "direct_web", label: "Web propia" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "manual", label: "Reserva manual" },
  { value: "walk_in", label: "Walk-in" },
];

export const ASSIGNMENT_MODES = [
  { value: "auto", label: "Automática", hint: "El motor de reparto elige la unidad." },
  { value: "manual", label: "Manual", hint: "Eliges tú y queda registrado el motivo." },
  { value: "later", label: "Más tarde", hint: "La reserva queda sin unidad física asignada." },
];

export const RESERVATIONS_DEMO_NOTE =
  "Prototipo de la ficha de reserva. Los datos son de demostración y las "
  + "acciones solo cambian esta vista previa: no hay backend, ni pasarela de "
  + "pago, ni channel manager conectado.";
